import gql from 'graphql-tag';
import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import {
  Box,
  Card,
  CardContent,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { ErrorBoundary } from 'react-error-boundary';

import { Analytics } from '@watershed/analytics/analyticsUtils';
import { Teams } from '@watershed/constants/teams';
import workOsPortalRoutes from '@watershed/constants/workOsPortalRoutes';
import ArrowRightIcon from '@watershed/icons/components/ArrowRight';
import ChevronDownIcon from '@watershed/icons/components/ChevronDown';
import LogOutIcon from '@watershed/icons/components/LogOut';
import SendIcon from '@watershed/icons/components/Send';
import WarningIcon from '@watershed/icons/components/Warning';
import Callout from '@watershed/shared-frontend/components/Callout';
import LoadingPage from '@watershed/shared-frontend/components/LoadingPage';
import PageContainer from '@watershed/shared-frontend/components/PageContainer';
import useLogout from '@watershed/shared-frontend/hooks/useLogout';
import { useOwner } from '@watershed/shared-frontend/hooks/useOwner';
import { getGqlResultDataBang } from '@watershed/shared-frontend/utils/errorUtils';
import isFetchingOrStale from '@watershed/shared-frontend/utils/isFetchingOrStale';
import notifyError from '@watershed/shared-frontend/utils/notifyError';
import {
  SUPPORT_EMAIL_ADDRESS,
  SUPPORT_EMAIL_LINK,
} from '@watershed/shared-universal/dashboardRoutes';
import flattenConnection from '@watershed/shared-universal/utils/flattenConnection';
import Button from '@watershed/ui-core/components/Button';
import IndexTable, {
  IndexTableCellCopy,
} from '@watershed/ui-core/components/IndexTable';
import Logo from '@watershed/ui-core/components/Logo';
import Link from '@watershed/ui-core/components/TextLink';
import TextLink from '@watershed/ui-core/components/TextLink';

import { useGetSsoConfigPageQuery } from '@watershed/app-dashboard/generated/urql';

import { Layouts } from '../layouts/LayoutEnum';
import { useUserContext } from '../utils/UserContext';

gql`
  query GetSsoConfigPage @withOwner(owner: ApplicationPlatform) {
    userHasPermission(permission: ManageSingleSignOn)
    organization {
      id
      workosOrgId
      users(filters: { isAdmin: true }) {
        edges {
          node {
            id
            name
            email
          }
        }
      }
      hasActiveWorkosConnection
    }
  }
`;

const userBarSx: SxProps<Theme> = {
  padding: (theme) => theme.spacing(1, 0, 2),
  '& button': {
    width: (theme) => `calc(100% + ${theme.spacing(2)})`,
    justifyContent: 'space-between',
    margin: (theme) => theme.spacing(0, -1),
    padding: (theme) => theme.spacing(1),
    '& svg': {
      transition: (theme) => theme.transitions.create('transform'),
    },
  },
};

const userBarMenuSx: SxProps<Theme> = {
  width: 228,
};

const userBarMenuIconSx: SxProps<Theme> = {
  minWidth: 36,
};

const userBarMenuButtonSx: SxProps<Theme> = {
  letterSpacing: 'inherit',
  width: '100% !important',
};

// Similar to the UserBar in the SideBar, but simplified for this context.
function SsoConfigUserMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const userContext = useUserContext();
  const onLogout = useLogout();

  return (
    <Box sx={userBarSx}>
      <Button
        variant="text"
        endIcon={
          <ChevronDownIcon
            color="secondary.dark"
            style={{
              transform: anchorEl === null ? undefined : 'rotate(180deg)',
            }}
          />
        }
        aria-controls="user-menu"
        aria-haspopup="true"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {userContext.userName}
      </Button>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: userBarMenuSx,
        }}
      >
        <MenuItem
          component="a"
          href={SUPPORT_EMAIL_LINK}
          onClick={() => Analytics.action('emailWatershed')}
        >
          <ListItemIcon sx={userBarMenuIconSx}>
            <SendIcon />
          </ListItemIcon>
          <Trans>Email Watershed</Trans>
        </MenuItem>
        <MenuItem
          component="button"
          onClick={onLogout}
          sx={userBarMenuButtonSx}
        >
          <ListItemIcon sx={userBarMenuIconSx}>
            <LogOutIcon />
          </ListItemIcon>
          <Trans>Sign out…</Trans>
        </MenuItem>
      </Menu>
    </Box>
  );
}

function Header() {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <a
        href="https://watershed.com"
        target="_blank"
        rel="noreferrer"
        style={{ display: 'block', textDecoration: 'none' }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Logo size={40} />
          <Typography variant="h2">
            <Trans>Watershed</Trans>
          </Typography>
        </Box>
      </a>
      <SsoConfigUserMenu />
    </Box>
  );
}

interface Admin {
  id: string;
  name: string;
  email: string;
}

function AdminList({ admins }: { admins: Array<Admin> }) {
  if (admins.length === 0) {
    return (
      <Typography variant="body2">
        <Trans>
          If you have any questions, contact one of your organization’s
          Watershed admins.
        </Trans>
      </Typography>
    );
  }

  const adminRows = admins.map((admin) => ({
    id: admin.id,
    fields: {
      name: admin.name,
      email: (
        <IndexTableCellCopy snackbarText="Copied email address to clipboard">
          {admin.email}
        </IndexTableCellCopy>
      ),
    },
  }));

  return (
    <Typography component="div" variant="body2">
      <Box marginBottom={1}>
        <Trans>
          If you have any questions, contact one of your organization’s
          Watershed admins:
        </Trans>
      </Box>
      <IndexTable
        columns={['name', 'email']}
        rows={adminRows}
        hideHead={true}
        // Override IndexTable's padding.
        paddingLeft={0}
        paddingRight={0}
        paddingBottom={0}
      />
    </Typography>
  );
}

function Footer() {
  return (
    <Typography variant="body2" component="div">
      <Stack direction="row" gap={4} justifyContent="flex-end">
        <Link href="https://watershed.com/about-us">
          <Trans context="Link copy for information about Watershed">
            About Watershed
          </Trans>
        </Link>
        <Link href="https://watershed.com/privacy">
          <Trans context="Link copy for information on our Privacy Policy">
            Privacy
          </Trans>
        </Link>
      </Stack>
    </Typography>
  );
}

function FullPageError() {
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <WarningIcon size={128} marginBottom={1} color="error.main" />
      <Typography variant="h1" align="center" gutterBottom>
        <Trans>Something went wrong</Trans>
      </Typography>
      <Typography variant="body2">
        <Trans>
          Please reload the page. If the problem continues, email{' '}
          <Link href={SUPPORT_EMAIL_LINK}>{SUPPORT_EMAIL_ADDRESS}</Link>.
        </Trans>
      </Typography>
    </Box>
  );
}

export default function SsoConfig() {
  useOwner(Teams.ApplicationPlatform);
  const { orgName } = useUserContext();
  const [result] = useGetSsoConfigPageQuery();
  if (isFetchingOrStale(result)) {
    return <LoadingPage />;
  }
  const data = getGqlResultDataBang(result, { requireUserHasPermission: true });
  const hasWorkosOrgId = data.organization.workosOrgId !== null;

  const workosLink = hasWorkosOrgId ? (
    data.organization.hasActiveWorkosConnection ? (
      <Button
        href={workOsPortalRoutes.sso}
        color="secondary"
        endIcon={<ArrowRightIcon />}
      >
        <Trans>Edit Watershed SSO configuration for {orgName}</Trans>
      </Button>
    ) : (
      <Button
        href={workOsPortalRoutes.sso}
        color="primary"
        endIcon={<ArrowRightIcon />}
      >
        <Trans> Configure Watershed SSO for {orgName}</Trans>
      </Button>
    )
  ) : (
    <Callout
      variant="warning"
      description={
        <Typography>
          <Trans>
            The organization {orgName} does not have SSO enabled. Please email{' '}
            <TextLink href={SUPPORT_EMAIL_LINK}>
              {SUPPORT_EMAIL_ADDRESS}
            </TextLink>{' '}
            to enable SSO for your organization.
          </Trans>
        </Typography>
      }
    />
  );

  const admins = flattenConnection(data.organization?.users);
  return (
    <PageContainer isFullPage={false} maxWidth="md" component="div">
      <Stack gap={2} paddingX={1} paddingY={2}>
        <Header />
        <Card sx={{ flexGrow: 1, border: 0 }}>
          <CardContent>
            <Box padding={2}>
              <ErrorBoundary
                FallbackComponent={FullPageError}
                onError={(error) => notifyError(error)}
              >
                <Box marginBottom={4}>
                  <Typography variant="h1" component="h1">
                    <Trans>Configure single sign-on (SSO)</Trans>
                  </Typography>
                </Box>

                <Typography component="div" variant="body2">
                  <Box marginY={2}>
                    <Trans>
                      The <strong>{orgName}</strong> team is using Watershed to
                      implement a world-class climate program.
                    </Trans>
                  </Box>

                  {hasWorkosOrgId && (
                    <Box marginY={2}>
                      <Trans>
                        You have been asked to configure SSO for the
                        organization. Watershed uses{' '}
                        <Link href="https://workos.com/" target="_blank">
                          WorkOS
                        </Link>{' '}
                        for SSO management.
                      </Trans>
                    </Box>
                  )}
                </Typography>

                <Box marginTop={2}>
                  {workosLink}
                  {hasWorkosOrgId && (
                    <Box marginTop={4}>
                      <AdminList admins={admins} />
                    </Box>
                  )}
                </Box>
              </ErrorBoundary>
            </Box>
          </CardContent>
        </Card>
        <Footer />
      </Stack>
    </PageContainer>
  );
}

SsoConfig.layout = Layouts.LoggedInFullBleed;
